import React, { useEffect, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { useDevToolsStore } from '../../store/devToolsStore';
import { isTauri } from '../../utils/tauri';

function formatUptime(sec) {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function cpuClass(val) {
  if (val >= 50) return 'perf-cpu--high';
  if (val >= 20) return 'perf-cpu--mid';
  return 'perf-cpu--low';
}

function CpuBar({ val }) {
  const pct = Math.min(100, Math.max(0, val));
  const cls = cpuClass(val);
  return (
    <div className={`perf-cpu-cell ${cls}`}>
      <div className="perf-cpu-bar-wrap">
        <div className="perf-cpu-bar" style={{ width: `${pct}%` }} />
      </div>
      <span className="perf-cpu-val">{val}%</span>
    </div>
  );
}

function MemBar({ val, max }) {
  const pct = max > 0 ? Math.min(100, (val / max) * 100) : 0;
  return (
    <div className="perf-mem-cell">
      <div className="perf-cpu-bar-wrap">
        <div className="perf-mem-bar" style={{ width: `${pct}%` }} />
      </div>
      <span className="perf-mem-val">{val} MB</span>
    </div>
  );
}

export default function PerformancePanel({ active = true }) {
  const activeTab = useDevToolsStore((s) => s.activeTab);
  const isActive = active && activeTab === 'performance';
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isTauri() || !isActive) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const data = await invoke('get_process_metrics');
        if (!cancelled) {
          setMetrics(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    };

    poll();
    const id = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isActive]);

  if (!isTauri()) {
    return (
      <div className="performance-panel performance-panel--placeholder">
        Performance metrics require Apiboo Desktop
      </div>
    );
  }

  if (error) {
    return <div className="performance-panel performance-panel--placeholder">{error}</div>;
  }

  if (!metrics) {
    return <div className="performance-panel performance-panel--placeholder">Loading…</div>;
  }

  const { app, cumulative, processes } = metrics;
  const maxMem = Math.max(...(processes || []).map((p) => p.memory_mb), 1);

  return (
    <div className="performance-panel">
      <div className="performance-panel__summary">
        <div className="performance-stat">
          <span className="performance-stat__label">PID</span>
          <span className="performance-stat__value">{app.pid}</span>
        </div>
        <div className="performance-stat">
          <span className="performance-stat__label">Uptime</span>
          <span className="performance-stat__value">{formatUptime(app.uptime_sec)}</span>
        </div>
        <div className="performance-stat">
          <span className="performance-stat__label">CPU</span>
          <span className="performance-stat__value">{app.cpu_percent}%</span>
        </div>
        <div className="performance-stat">
          <span className="performance-stat__label">Memory</span>
          <span className="performance-stat__value">{app.memory_mb.toFixed(1)} MB</span>
        </div>
        <div className="performance-stat">
          <span className="performance-stat__label">Processes</span>
          <span className="performance-stat__value">{cumulative.process_count}</span>
        </div>
      </div>

      <div className="performance-panel__table-wrap panel-scroll">
        <table className="performance-panel__table">
          <thead>
            <tr>
              <th>Category</th>
              <th>PID</th>
              <th>Name</th>
              <th>CPU %</th>
              <th>Memory</th>
            </tr>
          </thead>
          <tbody>
            {(processes || []).map((p) => (
              <tr key={p.pid}>
                <td>{p.category}</td>
                <td className="perf-pid">{p.pid}</td>
                <td title={p.name}>{p.name}</td>
                <td>
                  <CpuBar val={p.cpu_percent} />
                </td>
                <td>
                  <MemBar val={p.memory_mb} max={maxMem} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
